import {
  Alert,
  AlertTitle,
  Badge,
  Box,
  Flex,
  Skeleton,
  Text,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { assignRoot, unassignRoot } from "../../api/customer";
import { getFeatureFlags, FeatureFlagsResponse } from "../../api/feature-flags";
import { allowMapiAccess, revokeMapiAccess } from "../../api/internal";
import { Cohort } from "../../api/schemas/cohort";
import {
  ExtendedOrganization,
  ExtendedOrganizationWithMapiOrg,
  Organization,
} from "../../api/schemas/organization";
import { CxFeatureFlagDetails, CxFeatureFlagStatus } from "../../domain/app-config";
import { CustomerMapEntry } from "../../domain/customer";
import { areRolesIncluded, roleAdmin } from "../../shared/access-control";
import { useAppContext } from "../contexts/app/index";
import { Accordion } from "../shared/Accordion";
import { Button, ButtonBoolean } from "../shared/Button";
import {
  baseCxHook,
  cxDataHook,
  setCxHieEnabledStatus,
  setCxIsRoot,
  setCxMapiAccess,
  setCxOrganzation,
} from "../shared/state-management";
import { Tag, TagBoolean } from "../shared/Tag";
import useMetriportToast from "../shared/toast";
import { captureAndDisplayError, isItVendor, isProvider } from "../shared/util";
import CohortForm from "./cohort/CohortForm";
import CohortDeleteButton from "./cohort/CohortDeleteButton";
import FeatureFlagForm from "./feature-flags/FeatureFlagForm";
import PatientLookupModal from "./patient-lookup/PatientLookupModal";
import RosterUploadForm from "./roster/RosterUploadForm";
import { setHieOrganizationActive } from "./organization/hie-active";
import OrganizationForm from "./organization/OrganizationInternalForm";

export default function CustomerPage() {
  const { state, dispatch } = useAppContext();
  const { cxId } = useParams();
  const navigate = useNavigate();
  const toast = useMetriportToast();

  const [customer, setCustomer] = useState<CustomerMapEntry | undefined>(undefined);
  const [isRoot, setIsRoot] = useState<boolean | undefined>(undefined);
  const [cwStatus, setCwStatus] = useState<CxFeatureFlagDetails | undefined>(undefined);
  const [cqStatus, setCqStatus] = useState<CxFeatureFlagDetails | undefined>(undefined);
  const [ehexStatus, setEhexStatus] = useState<CxFeatureFlagDetails | undefined>(undefined);
  const [epicStatus, setEpicStatus] = useState<CxFeatureFlagDetails | undefined>(undefined);
  const [demoAugStatus, setDemoAugStatus] = useState<CxFeatureFlagDetails | undefined>(undefined);
  const [adtRosterUploadStatus, setAdtRosterUploadStatus] = useState<
    CxFeatureFlagDetails | undefined
  >(undefined);
  const [adtDataVisibleStatus, setAdtDataVisibleStatus] = useState<
    CxFeatureFlagDetails | undefined
  >(undefined);
  const [aiBriefStatus, setAiBriefStatus] = useState<CxFeatureFlagDetails | undefined>(undefined);
  const [hasMapiAccess, setHasMapiAccess] = useState<boolean | undefined>(undefined);
  const [organization, setOrganization] = useState<ExtendedOrganization | undefined>(undefined);
  const { isOpen: isOpenFF, onOpen: onOpenFF, onClose: onCloseFF } = useDisclosure();
  const {
    isOpen: isOpenInternal,
    onOpen: onOpenInternal,
    onClose: onCloseInternal,
  } = useDisclosure();
  const { isOpen: isOpenCohort, onOpen: onOpenCohort, onClose: onCloseCohort } = useDisclosure();
  const [cohorts, setCohorts] = useState<Cohort[] | undefined>(undefined);
  const [selectedCohort, setSelectedCohort] = useState<Cohort | undefined>(undefined);
  const [loadingCohorts, setLoadingCohorts] = useState<boolean>(false);
  const {
    isOpen: isOpenRosterUpload,
    onOpen: onOpenRosterUpload,
    onClose: onCloseRosterUpload,
  } = useDisclosure();
  const {
    isOpen: isOpenPatientLookup,
    onOpen: onOpenPatientLookup,
    onClose: onClosePatientLookup,
  } = useDisclosure();

  const [loadingBaseData, setLoadingBaseData] = useState<boolean>(false);
  const [loadingCxData, setLoadingCxData] = useState<boolean>(false);
  const [loadingHieOrgs, setLoadingHieOrgs] = useState<boolean>(false);
  const [onClickRunningCw, setOnClickRunningCw] = useState<boolean>(false);
  const [onClickRunningCq, setOnClickRunningCq] = useState<boolean>(false);
  const [allFeatureFlags, setAllFeatureFlags] = useState<FeatureFlagsResponse | undefined>(
    undefined
  );
  const [loadingFeatureFlags, setLoadingFeatureFlags] = useState<boolean>(false);

  const rootActionRequiredRoles = [roleAdmin];

  // Get all feature flags where this customer's cxId is in the values array
  function getActiveFeatureFlagsForCustomer(): string[] {
    if (!allFeatureFlags || !cxId) return [];
    return Object.entries(allFeatureFlags.featureFlags)
      .filter(([, flag]) => flag.enabled && flag.values?.includes(cxId))
      .map(([name]) => name)
      .sort();
  }

  const activeFlags = getActiveFeatureFlagsForCustomer();

  useEffect(() => {
    if (state.isLoaded && cxId) {
      setLoadingBaseData(true);
      baseCxHook({
        state,
        dispatch,
        cxId,
        setCustomer,
        setIsRoot,
        setCwStatus,
        setCqStatus,
        setEhexStatus,
        setEpicStatus,
        setDemoAugStatus,
        setAdtRosterUploadStatus,
        setAdtDataVisibleStatus,
        setAiBriefStatus,
        setHasMapiAccess,
        toast,
      });
      setLoadingCxData(true);
      setLoadingHieOrgs(true);
      cxDataHook({
        state,
        dispatch,
        cxId,
        setOrganization,
        setCohorts,
        setLoadingCohorts,
        toast,
      });
      // Load all feature flags
      setLoadingFeatureFlags(true);
      getFeatureFlags()
        .then(setAllFeatureFlags)
        .catch(err => {
          console.error("Failed to load feature flags", err);
        })
        .finally(() => setLoadingFeatureFlags(false));
    }
  }, [state.isLoaded]);

  useEffect(() => {
    if (
      customer !== undefined &&
      isRoot !== undefined &&
      cwStatus !== undefined &&
      cqStatus !== undefined &&
      ehexStatus !== undefined &&
      epicStatus !== undefined &&
      demoAugStatus !== undefined &&
      adtRosterUploadStatus !== undefined &&
      adtDataVisibleStatus !== undefined &&
      aiBriefStatus !== undefined &&
      hasMapiAccess !== undefined
    ) {
      setLoadingBaseData(false);
    }
  }, [
    customer,
    isRoot,
    cwStatus,
    cqStatus,
    ehexStatus,
    epicStatus,
    demoAugStatus,
    adtRosterUploadStatus,
    adtDataVisibleStatus,
    aiBriefStatus,
    hasMapiAccess,
  ]);

  useEffect(() => {
    if (organization !== undefined) setLoadingCxData(false);
    if (
      organization &&
      organization.cqOrganization !== undefined &&
      organization.cwOrganization !== undefined
    ) {
      setLoadingHieOrgs(false);
    }
  }, [organization]);

  function openFFForm() {
    onOpenFF();
  }

  function closeFFForm() {
    onCloseFF();
  }

  function submitFFForm(cxId: string, newHieEnabledStatus: CxFeatureFlagStatus) {
    setCxHieEnabledStatus({
      cxId,
      state,
      dispatch,
      setCwStatus,
      setCqStatus,
      setEhexStatus,
      setEpicStatus,
      setDemoAugStatus,
      setAdtRosterUploadStatus,
      setAdtDataVisibleStatus,
      setAiBriefStatus,
      hieEnabledStatus: newHieEnabledStatus,
    });
    onCloseFF();
  }

  function showEhexNotImplemented() {
    return toast.error({
      title: `Not implemented!`,
      description:
        `Make sure you setup the EHEX Directory entry for this organization through email.\n` +
        `Once done, update the ehexActive flag using the "Update Organization" button.`,
      duration: 10_000,
    });
  }

  function openInternalForm() {
    onOpenInternal();
  }

  function closeInternalForm() {
    onCloseInternal();
  }

  function submitInternalForm(cxId: string, newOrganization: Organization) {
    setCxOrganzation({
      cxId,
      state,
      dispatch,
      setter: setOrganization,
      organization: newOrganization,
      cqOrganization: organization?.cqOrganization,
      cwOrganization: organization?.cwOrganization,
    });
    onCloseInternal();
  }

  async function handleIsRootChange(cxId: string, isRoot: boolean) {
    try {
      if (isRoot) {
        await unassignRoot(cxId);
      } else {
        await assignRoot(cxId);
      }

      setCxIsRoot({
        cxId,
        state,
        dispatch,
        setter: setIsRoot,
        isRoot: !isRoot,
      });
    } catch (error) {
      captureAndDisplayError({
        error,
        msg: "Failed to update root status",
        context: "handleIsRootChange",
        captureMsg: `root status update failed`,
        toast,
        extras: {
          cxId,
          isRoot,
        },
      });
    }
  }

  async function handleHasMapiAccessChange(cxId: string, hasMapiAccess: boolean) {
    try {
      if (hasMapiAccess) {
        await revokeMapiAccess(cxId);
      } else {
        await allowMapiAccess(cxId);
      }

      setCxMapiAccess({
        cxId,
        state,
        dispatch,
        setter: setHasMapiAccess,
        hasMapiAccess: !hasMapiAccess,
      });
    } catch (error) {
      captureAndDisplayError({
        error,
        msg: "Failed to update MAPI access",
        context: "handleHasMapiAccessChange",
        captureMsg: `MAPI access update failed`,
        toast,
        extras: {
          cxId,
          hasMapiAccess,
        },
      });
    }
  }

  function handleCohortDelete(cohortId: string) {
    setCohorts(cohorts => cohorts?.filter(cohort => cohort.id !== cohortId));
    setSelectedCohort(undefined);
  }

  return (
    <Box>
      <VStack p={10} align="start">
        {loadingBaseData ? (
          <Skeleton mt={5} h={100} w={"4xl"} />
        ) : (
          <>
            {customer !== undefined && (
              <Box>
                <Text h={10} pt={3} fontWeight={"bold"}>
                  Customer Info
                </Text>
                <Flex mb={2}>
                  <Tag id="cxId" label={`${customer.rootCustomer.id}`} canCopy={true} />
                  <Tag id="cxEmail" label={`${customer.rootCustomer.email}`} canCopy={true} />
                </Flex>
                <Box mt={6}>
                  <Text fontWeight="bold" mb={2}>
                    Active Feature Flags
                  </Text>
                  {loadingFeatureFlags ? (
                    <Skeleton h={8} w="200px" />
                  ) : activeFlags.length === 0 ? (
                    <Text color="gray.500" fontSize="sm">
                      No feature flags enabled for this customer
                    </Text>
                  ) : (
                    <Wrap gap={2}>
                      {activeFlags.map(flagName => (
                        <WrapItem key={flagName}>
                          <Badge colorScheme="green" fontSize="xs" px={2} py={1} borderRadius="md">
                            {flagName}
                          </Badge>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>
                <Flex gap={2} my={2}>
                  <Box mb={2}>
                    <Button
                      id="patientLookup"
                      onClick={onOpenPatientLookup}
                      label="Find Patient by External ID"
                      icon="info"
                      iconSide="left"
                    />
                    <PatientLookupModal
                      isOpen={isOpenPatientLookup}
                      onClose={onClosePatientLookup}
                      cxId={customer.rootCustomer.id}
                    />
                  </Box>
                  <Box mb={2}>
                    <Button
                      id="ehrIntegrations"
                      onClick={() =>
                        navigate(`/ehr/${encodeURIComponent(customer.rootCustomer.id)}`)
                      }
                      label="View EHR Integrations"
                      icon="info"
                      iconSide="left"
                    />
                  </Box>
                </Flex>
                <Box mt={50}>
                  <Text pt={3} fontWeight={"bold"}>
                    Customer Details
                  </Text>
                  {isRoot !== undefined && (
                    <Box mb={2}>
                      {areRolesIncluded(rootActionRequiredRoles, state.userRoles) ? (
                        <ButtonBoolean
                          id="isRoot"
                          check={isRoot}
                          disableOnClick={false}
                          onClick={async () =>
                            await handleIsRootChange(customer.rootCustomer.id, isRoot)
                          }
                          label="Root Status"
                          activeColor="green"
                          confirmTitle={`${isRoot ? "Disable" : "Enable"} root status?`}
                          confirmMessage={`Are you sure you want to ${
                            isRoot ? "disable" : "enable"
                          } root status for ${customer.rootCustomer.email} (${
                            customer.rootCustomer.id
                          })?`}
                          confirmText={isRoot ? "Disable" : "Enable"}
                          shouldAskForConfirmation
                        />
                      ) : (
                        <Flex>
                          <TagBoolean id="isRoot" check={isRoot} label={`Root Status`} />
                          <Text>Please contact an admin to update root status</Text>
                        </Flex>
                      )}
                    </Box>
                  )}
                  {isRoot !== undefined && isRoot ? (
                    <Box>
                      {cwStatus !== undefined &&
                        cqStatus !== undefined &&
                        ehexStatus !== undefined &&
                        epicStatus !== undefined &&
                        demoAugStatus !== undefined &&
                        adtRosterUploadStatus !== undefined &&
                        adtDataVisibleStatus !== undefined &&
                        aiBriefStatus !== undefined && (
                          <Box>
                            <Flex mb={2}>
                              <ButtonBoolean
                                id="cwStatus"
                                check={cwStatus.cxInFFValues}
                                disableOnClick={false}
                                onClick={openFFForm}
                                label={
                                  !cwStatus.ffEnabled
                                    ? `CW Enabled (CW NOT Globally Enabled)`
                                    : `CW Enabled`
                                }
                                activeColor="green"
                              />
                              <ButtonBoolean
                                id="cqStatus"
                                check={cqStatus.cxInFFValues}
                                disableOnClick={false}
                                onClick={openFFForm}
                                label={
                                  !cqStatus.ffEnabled
                                    ? `CQ Enabled (CQ NOT Globally Enabled)`
                                    : `CQ Enabled`
                                }
                                activeColor="green"
                              />
                              <ButtonBoolean
                                id="ehexStatus"
                                check={ehexStatus.cxInFFValues}
                                disableOnClick={false}
                                onClick={openFFForm}
                                label={
                                  !ehexStatus.ffEnabled
                                    ? `EHEX Enabled (EHEX NOT Globally Enabled)`
                                    : `EHEX Enabled`
                                }
                                activeColor="green"
                              />
                              <ButtonBoolean
                                id="epicStatus"
                                check={epicStatus.cxInFFValues}
                                disableOnClick={false}
                                onClick={openFFForm}
                                label={
                                  !epicStatus.ffEnabled
                                    ? `Epic Enabled (Epic NOT Globally Enabled)`
                                    : `Epic Enabled`
                                }
                                activeColor="green"
                              />
                              <ButtonBoolean
                                id="demoAugStatus"
                                check={demoAugStatus.cxInFFValues}
                                disableOnClick={false}
                                onClick={openFFForm}
                                label={
                                  !demoAugStatus.ffEnabled
                                    ? `Demo Aug Enabled (Demo Aug NOT Globally Enabled)`
                                    : `Demo Aug Enabled`
                                }
                                activeColor="green"
                              />
                            </Flex>
                            <Flex mb={2}>
                              <ButtonBoolean
                                id="adtRosterUploadStatus"
                                check={adtRosterUploadStatus.cxInFFValues}
                                disableOnClick={false}
                                onClick={openFFForm}
                                label="ADT Roster Upload"
                                activeColor="green"
                              />
                              <ButtonBoolean
                                id="adtDataVisibleStatus"
                                check={adtDataVisibleStatus.cxInFFValues}
                                disableOnClick={false}
                                onClick={openFFForm}
                                label="ADT Data Visible"
                                activeColor="green"
                              />
                              <ButtonBoolean
                                id="aiBriefStatus"
                                check={aiBriefStatus.cxInFFValues}
                                disableOnClick={false}
                                onClick={openFFForm}
                                label="AI Brief Enabled"
                                activeColor="green"
                              />
                            </Flex>
                            {(!cwStatus.ffEnabled ||
                              !cqStatus.ffEnabled ||
                              !ehexStatus.ffEnabled ||
                              !epicStatus.ffEnabled ||
                              !demoAugStatus.ffEnabled ||
                              !aiBriefStatus.ffEnabled) && (
                              <Alert status="error" mb={2} w={"max-content"} rounded="md">
                                <AlertTitle>
                                  At least one feature flag is not active globally - updating those
                                  feature flags will have no affect for the customer
                                </AlertTitle>
                              </Alert>
                            )}
                            <FeatureFlagForm
                              isOpen={isOpenFF}
                              onClose={closeFFForm}
                              onSubmit={submitFFForm}
                              cxId={customer.rootCustomer.id}
                              currentValues={{
                                cxsWithCWFeatureFlag: cwStatus,
                                cxsWithCQDirectFeatureFlag: cqStatus,
                                cxsWithEhexEnabled: ehexStatus,
                                cxsWithEpicEnabled: epicStatus,
                                cxsWithDemoAugEnabled: demoAugStatus,
                                cxsWithAdtRosterUploadEnabled: adtRosterUploadStatus,
                                cxsWithAdtDataVisibleEnabled: adtDataVisibleStatus,
                                cxsWithAiBriefEnabled: aiBriefStatus,
                              }}
                            />
                          </Box>
                        )}
                      {hasMapiAccess !== undefined && (
                        <Box>
                          <Flex mb={2}>
                            <ButtonBoolean
                              id="mapiAccess"
                              check={hasMapiAccess}
                              disableOnClick={false}
                              onClick={async () =>
                                await handleHasMapiAccessChange(
                                  customer.rootCustomer.id,
                                  hasMapiAccess
                                )
                              }
                              label="MAPI Access"
                              activeColor="green"
                              confirmTitle={`${hasMapiAccess ? "Revoke" : "Grant"} MAPI access?`}
                              confirmMessage={`Are you sure you want to ${
                                hasMapiAccess ? "revoke" : "grant"
                              } MAPI access for ${customer.rootCustomer.email} (${
                                customer.rootCustomer.id
                              })?`}
                              confirmText={hasMapiAccess ? "Revoke" : "Grant"}
                              shouldAskForConfirmation
                            />
                          </Flex>
                        </Box>
                      )}
                      <Box mt={6}>
                        <Text fontWeight={"bold"} mb={2}>
                          Manual Roster Upload
                        </Text>
                        <Button
                          id="uploadRoster"
                          onClick={onOpenRosterUpload}
                          label="Upload Roster"
                        />
                        <RosterUploadForm
                          isOpen={isOpenRosterUpload}
                          onClose={onCloseRosterUpload}
                          cxId={customer.rootCustomer.id}
                        />
                      </Box>
                      <Box mt={50}>
                        <Flex>
                          <Text pt={3} fontWeight={"bold"}>
                            Cohorts
                          </Text>
                          <Box ml={"auto"}>
                            <Button
                              id="createCohort"
                              onClick={() => {
                                setSelectedCohort(undefined);
                                onOpenCohort();
                              }}
                              label="Create Cohort"
                              icon="add"
                              iconSide="left"
                            />
                          </Box>
                        </Flex>
                        {loadingCohorts ? (
                          <Skeleton mt={2} h={50} w={"full"} />
                        ) : !cohorts || cohorts.length === 0 ? (
                          <Text color="gray.500" mt={2}>
                            No cohorts configured
                          </Text>
                        ) : (
                          <Flex wrap="wrap" gap={3} mt={2}>
                            {cohorts.map(cohort => (
                              <Box
                                key={cohort.id}
                                bg="gray.50"
                                border="1px solid"
                                borderColor="gray.300"
                                borderRadius="md"
                                cursor="pointer"
                                _hover={{
                                  bg: "gray.100",
                                  ".cohort-delete-button": {
                                    opacity: 1,
                                    transform: "scale(1)",
                                  },
                                }}
                                onClick={() => {
                                  setSelectedCohort(cohort);
                                  onOpenCohort();
                                }}
                                overflow="hidden"
                                minW="200px"
                                position="relative"
                              >
                                <Box
                                  className="cohort-delete-button"
                                  position="absolute"
                                  top={2}
                                  right={2}
                                  zIndex={1}
                                  opacity={0.75}
                                  transform="scale(0.9)"
                                  transition="opacity 120ms ease, transform 120ms ease"
                                >
                                  <CohortDeleteButton
                                    cxId={customer.rootCustomer.id}
                                    cohort={cohort}
                                    onDelete={handleCohortDelete}
                                    variant="icon"
                                  />
                                </Box>
                                <Box
                                  h="4px"
                                  bg={cohort.color === "white" ? "gray.200" : cohort.color}
                                />
                                <Box px={3} py={2} pr={10}>
                                  <Flex align="center" gap={2}>
                                    <Box
                                      w="10px"
                                      h="10px"
                                      borderRadius="full"
                                      bg={cohort.color === "white" ? "gray.200" : cohort.color}
                                      border={cohort.color === "white" ? "1px solid" : undefined}
                                      borderColor="gray.300"
                                      flexShrink={0}
                                    />
                                    <Text fontWeight="medium" noOfLines={1}>
                                      {cohort.name}
                                    </Text>
                                  </Flex>
                                  <Text fontSize="xs" color="gray.500" mt={1}>
                                    {cohort.size ?? 0} patient(s)
                                  </Text>
                                  <Text fontSize="xs" color="gray.400" fontFamily="mono">
                                    {cohort.id}
                                  </Text>
                                  <Flex gap={1} mt={2} wrap="wrap">
                                    <Text
                                      fontSize="2xs"
                                      px={1.5}
                                      py={0.5}
                                      borderRadius="sm"
                                      bg={
                                        cohort.settings.monitoring.adt.enabled
                                          ? "green.100"
                                          : "gray.100"
                                      }
                                      color={
                                        cohort.settings.monitoring.adt.enabled
                                          ? "green.700"
                                          : "gray.500"
                                      }
                                    >
                                      ADT
                                    </Text>
                                    <Text
                                      fontSize="2xs"
                                      px={1.5}
                                      py={0.5}
                                      borderRadius="sm"
                                      bg={
                                        cohort.settings.monitoring.hie.enabled
                                          ? "green.100"
                                          : "gray.100"
                                      }
                                      color={
                                        cohort.settings.monitoring.hie.enabled
                                          ? "green.700"
                                          : "gray.500"
                                      }
                                    >
                                      HIE
                                    </Text>
                                    <Text
                                      fontSize="2xs"
                                      px={1.5}
                                      py={0.5}
                                      borderRadius="sm"
                                      bg={
                                        cohort.settings.monitoring.pharmacy.notifications ||
                                        cohort.settings.monitoring.pharmacy.schedule.enabled
                                          ? "green.100"
                                          : "gray.100"
                                      }
                                      color={
                                        cohort.settings.monitoring.pharmacy.notifications ||
                                        cohort.settings.monitoring.pharmacy.schedule.enabled
                                          ? "green.700"
                                          : "gray.500"
                                      }
                                    >
                                      Pharmacy
                                    </Text>
                                    <Text
                                      fontSize="2xs"
                                      px={1.5}
                                      py={0.5}
                                      borderRadius="sm"
                                      bg={
                                        cohort.settings.monitoring.laboratory.notifications
                                          ? "green.100"
                                          : "gray.100"
                                      }
                                      color={
                                        cohort.settings.monitoring.laboratory.notifications
                                          ? "green.700"
                                          : "gray.500"
                                      }
                                    >
                                      Lab
                                    </Text>
                                  </Flex>
                                </Box>
                              </Box>
                            ))}
                          </Flex>
                        )}
                        <CohortForm
                          isOpen={isOpenCohort}
                          onClose={() => {
                            onCloseCohort();
                            setSelectedCohort(undefined);
                          }}
                          onSubmit={newCohort => {
                            setCohorts(cohorts => {
                              if (!cohorts) return [newCohort];
                              if (selectedCohort) {
                                return cohorts.map(cohort =>
                                  cohort.id === newCohort.id ? newCohort : cohort
                                );
                              }
                              return [...cohorts, newCohort];
                            });
                            onCloseCohort();
                            setSelectedCohort(undefined);
                          }}
                          onDelete={cohortId => {
                            handleCohortDelete(cohortId);
                            onCloseCohort();
                          }}
                          cxId={customer.rootCustomer.id}
                          cohort={selectedCohort}
                        />
                      </Box>
                      {loadingCxData ? (
                        <Skeleton mt={5} h={100} w={"full"} />
                      ) : (
                        <Box mt={50}>
                          {organization !== undefined && (
                            <Box>
                              {organization.organization && (
                                <Box>
                                  <Text pt={3} fontWeight={"bold"}>
                                    Organization Info
                                  </Text>
                                  <Flex mb={2}>
                                    <Tag
                                      id="orgId"
                                      label={`${organization.organization.id}`}
                                      canCopy={true}
                                    />
                                    <Tag
                                      id="orgType"
                                      label={`${organization.organization.businessType}`}
                                      icon="info"
                                    />
                                  </Flex>
                                </Box>
                              )}
                              <Flex mt={50} mb={5}>
                                <Text pt={3} fontWeight={"bold"}>
                                  Organization Details
                                </Text>
                                <Box ml={"auto"}>
                                  {!organization.organization && (
                                    <Button
                                      id="createOrg"
                                      onClick={openInternalForm}
                                      label="Create Organization"
                                      icon="add"
                                      iconSide="left"
                                    />
                                  )}
                                </Box>
                              </Flex>
                              {!organization.organization && <Text>No organization</Text>}
                              <Accordion
                                id="organization"
                                entries={
                                  organization.organization && organization.organization.id
                                    ? [[organization.organization.id, organization]]
                                    : ([] as [string, object][])
                                }
                                getLabel={(payload: object) => {
                                  const castPayload = payload as ExtendedOrganizationWithMapiOrg;
                                  return castPayload.organization.name;
                                }}
                                labelOnClick={() => {
                                  navigate(`/customer/${customer.rootCustomer.id}/facilities`);
                                }}
                                getLabelActions={
                                  isProvider(organization)
                                    ? (payload: object) => {
                                        const castPayload =
                                          payload as ExtendedOrganizationWithMapiOrg;
                                        return (
                                          <Flex>
                                            {castPayload.organization.cwApproved ? (
                                              <ButtonBoolean
                                                id="isCWActive"
                                                check={castPayload.organization.cwActive === true}
                                                disableOnClick={false}
                                                onClick={async () =>
                                                  await setHieOrganizationActive({
                                                    cxId: customer.rootCustomer.id,
                                                    state,
                                                    dispatch,
                                                    setOrganization,
                                                    hie: "commonwell",
                                                    organization: castPayload,
                                                    toast,
                                                    setRunning: setOnClickRunningCw,
                                                  })
                                                }
                                                label="CW Active"
                                                activeColor="green"
                                                icon={onClickRunningCw ? "spinner" : undefined}
                                                iconFalse={onClickRunningCw ? "spinner" : undefined}
                                                confirmTitle={`${
                                                  castPayload.organization.cwActive
                                                    ? "Disable"
                                                    : "Enable"
                                                } CW Active?`}
                                                confirmMessage={`Are you sure you want to ${
                                                  castPayload.organization.cwActive
                                                    ? "disable"
                                                    : "enable"
                                                } CW Active for organization ${
                                                  castPayload.organization.name
                                                }?`}
                                                confirmText={
                                                  castPayload.organization.cwActive
                                                    ? "Disable"
                                                    : "Enable"
                                                }
                                                shouldAskForConfirmation
                                              />
                                            ) : (
                                              <Tag id="cwApproved" label="CW Not Approved" />
                                            )}
                                            {castPayload.organization.cqApproved ? (
                                              <ButtonBoolean
                                                id="isCQActive"
                                                check={castPayload.organization.cqActive === true}
                                                disableOnClick={false}
                                                onClick={async () =>
                                                  await setHieOrganizationActive({
                                                    cxId: customer.rootCustomer.id,
                                                    state,
                                                    dispatch,
                                                    setOrganization,
                                                    hie: "carequality",
                                                    organization: castPayload,
                                                    toast,
                                                    setRunning: setOnClickRunningCq,
                                                  })
                                                }
                                                label="CQ Active"
                                                activeColor="green"
                                                icon={onClickRunningCq ? "spinner" : undefined}
                                                iconFalse={onClickRunningCq ? "spinner" : undefined}
                                                css={{ ml: 2 }}
                                                confirmTitle={`${
                                                  castPayload.organization.cqActive
                                                    ? "Disable"
                                                    : "Enable"
                                                } CQ Active?`}
                                                confirmMessage={`Are you sure you want to ${
                                                  castPayload.organization.cqActive
                                                    ? "disable"
                                                    : "enable"
                                                } CQ Active for organization ${
                                                  castPayload.organization.name
                                                }?`}
                                                confirmText={
                                                  castPayload.organization.cqActive
                                                    ? "Disable"
                                                    : "Enable"
                                                }
                                                shouldAskForConfirmation
                                              />
                                            ) : (
                                              <Tag id="cqApproved" label="CQ Not Approved" />
                                            )}
                                            {castPayload.organization.ehexApproved ? (
                                              <ButtonBoolean
                                                id="isEhexActive"
                                                check={castPayload.organization.ehexActive === true}
                                                disableOnClick={false}
                                                onClick={showEhexNotImplemented}
                                                label="EHEX Active"
                                                activeColor="green"
                                                css={{ ml: 2 }}
                                                confirmTitle={`${
                                                  castPayload.organization.ehexActive
                                                    ? "Disable"
                                                    : "Enable"
                                                } EHEX Active?`}
                                                confirmMessage={`Are you sure you want to ${
                                                  castPayload.organization.ehexActive
                                                    ? "disable"
                                                    : "enable"
                                                } EHEX Active for organization ${
                                                  castPayload.organization.name
                                                }?`}
                                                confirmText={
                                                  castPayload.organization.ehexActive
                                                    ? "Disable"
                                                    : "Enable"
                                                }
                                                shouldAskForConfirmation
                                              />
                                            ) : (
                                              <Tag id="ehexApproved" label="EHEX Not Approved" />
                                            )}
                                          </Flex>
                                        );
                                      }
                                    : () => {
                                        return (
                                          <Tag
                                            id="orgType"
                                            label={`No HIE Actions`}
                                            icon="info"
                                            color="orange"
                                          />
                                        );
                                      }
                                }
                                getPanelActions={() => {
                                  return (
                                    <Button
                                      id="updateOrg"
                                      onClick={openInternalForm}
                                      label="Update Organization"
                                      icon="edit"
                                      iconSide="left"
                                    />
                                  );
                                }}
                                getPanelInfo={() => {
                                  if (loadingHieOrgs) return <Skeleton mt={5} h={100} w={"full"} />;
                                }}
                                getPanelInfoDivider={!loadingHieOrgs}
                                getPanelInfoJsonExcludeKeys={
                                  isItVendor(organization)
                                    ? ["cqOrganization", "cwOrganization"]
                                    : []
                                }
                              />
                              {isOpenInternal && (
                                <OrganizationForm
                                  isOpen={isOpenInternal}
                                  onClose={closeInternalForm}
                                  onSubmit={submitInternalForm}
                                  cxId={customer.rootCustomer.id}
                                  currentOrg={organization.organization ?? undefined}
                                />
                              )}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Text mb={5}>No further information available for non-Root customers.</Text>
                  )}
                </Box>
              </Box>
            )}
          </>
        )}
      </VStack>
    </Box>
  );
}
